// Global Instructions Rule Applied!

import { fetchDealById, createDeal, updateDeal } from '../../../app_modules/deals/services/dealsService.ts'
import { setDeals, updateDealRecord, addDeal } from './deal_actions.ts'
import { markExecutionBoardStale } from '../deal_execution/index.ts'
import { DealTarget } from '../../interfaces/deals.ts'
import { DealRecord } from '../../data_models/deal_record.ts'
import { AppDispatch } from '../../store.ts'
import { fetchDealsPaginated } from '../../../app_modules/deals/services/dealsService.ts'

/**Fetches a deal by ID. */
const fetchDealByIdService = async (
  dealId: string | number,
  accessToken: string | null,
  dispatch: AppDispatch
): Promise<{ data: DealRecord | null; error?: string }> => {
  try {
    const response = await fetchDealById(dealId, accessToken)

    if (response.error) {
      return { data: null, error: response.error }
    }

    return { data: response.data as DealRecord | null }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch deal'
    return { data: null, error: errorMessage }
  }
}

/**Creates a new deal and dispatches addDeal action. */
export const createDealService = async (
  dealData: Record<string, any>,
  accessToken: string | null,
  dispatch: AppDispatch
): Promise<{ data: DealRecord | null; error?: string }> => {
  try {
    const response = await createDeal(dealData, accessToken)

    if (response.error) {
      return { data: null, error: response.error }
    }

    if (response.data) {
      dispatch(addDeal({ deal: response.data as DealRecord }))
      // Deal creation can auto-enroll the deal in its execution pipeline
      // server-side (process_new_deal → deal_execution__enter); the response
      // carries no signal either way, so flag the execution board stale and
      // let its hook refetch with its own filters.
      dispatch(markExecutionBoardStale())
    }

    return { data: response.data as DealRecord | null }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create deal'
    return { data: null, error: errorMessage }
  }
}

/**Updates an existing deal and dispatches updateDealRecord action. */
const updateDealService = async (
  dealId: string | number,
  dealData: Record<string, any>,
  accessToken: string | null,
  dispatch: AppDispatch
): Promise<{ data: DealRecord | null; error?: string }> => {
  try {
    const response = await updateDeal(dealId, dealData, accessToken)

    if (response.error) {
      return { data: null, error: response.error }
    }

    if (response.data) {
      const updatedDeal = response.data as DealRecord
      const idString = dealId.toString()

      dispatch(
        updateDealRecord({
          dealId: idString,
          updates: updatedDeal
        })
      )
    }

    return { data: response.data as DealRecord | null }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update deal'
    return { data: null, error: errorMessage }
  }
}
